import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { RiAddLine, RiPagesLine, RiUserAddLine } from '@remixicon/react'
import {
  ONBOARDING_LIMITS,
  type OnboardingSession,
  type OnboardingSessionCreated,
  type OnboardingTemplateOption,
  type PrivacyNotice,
} from 'shared'
import {
  Alert,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  Input,
  PageHeader,
  SectionSpinner,
  Select,
  Textarea,
} from '../../components/ui'
import { CodeReveal, SessionStatus } from '../../components/onboarding/SessionParts'
import LandingSettingsDialog from '../../components/onboarding/LandingSettingsDialog'
import { isAdmin as isAdminUser, useAuth } from '../../contexts/AuthContext'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'
import { formatDate } from '../../lib/dates'

/** The choice of writing the notice here instead of taking one from the library. */
const CUSTOM = 'custom'

function NewSessionDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: () => void
}) {
  const { t } = useTranslation()
  const [templates, setTemplates] = useState<OnboardingTemplateOption[] | null>(null)
  const [name, setName] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [max, setMax] = useState(String(ONBOARDING_LIMITS.defaultMaxApplications))
  const [notice, setNotice] = useState('')
  // The library, once loaded; the choice is a notice's id or CUSTOM.
  const [library, setLibrary] = useState<PrivacyNotice[] | null>(null)
  const [noticeChoice, setNoticeChoice] = useState(CUSTOM)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [created, setCreated] = useState<OnboardingSessionCreated | null>(null)

  useEffect(() => {
    if (!open || templates) return
    api
      .get<{ data: OnboardingTemplateOption[] }>('/api/onboarding/templates')
      .then((response) => {
        setTemplates(response.data)
        // The template new companies start with is the natural default.
        setTemplateId((response.data.find((template) => template.is_kickoff) ?? response.data[0])?.id ?? '')
      })
      .catch((err) => setError(toErrorMessage(err) ?? 'error'))
  }, [open, templates])

  useEffect(() => {
    if (!open) return
    api
      .get<{ data: PrivacyNotice[] }>('/api/admin/privacy-notices')
      .then((response) => {
        setLibrary(response.data)
        // The admin's default is what a new session starts with.
        setNoticeChoice(response.data.find((item) => item.is_default)?.id ?? CUSTOM)
      })
      // Without the library the session can still be opened with a text of its own.
      .catch(() => setLibrary([]))
  }, [open])

  const chosen = library?.find((item) => item.id === noticeChoice) ?? null

  const close = (next: boolean) => {
    onOpenChange(next)
    if (!next) {
      // Closing after creating clears the form, and the code with it.
      setCreated(null)
      setName('')
      setExpiresAt('')
      setNotice('')
      setError(null)
    }
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setSaving(true)
    setError(null)

    try {
      const response = await api.post<{ data: OnboardingSessionCreated }>('/api/onboarding/sessions', {
        name,
        template_id: templateId,
        expires_at: expiresAt,
        max_applications: Number(max),
        ...(chosen ? { privacy_notice_id: chosen.id } : { privacy_notice: notice }),
      })
      setCreated(response.data)
      onCreated()
    } catch (err) {
      setError(toErrorMessage(err) ?? 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('onboarding.newTitle')}</DialogTitle>
          <DialogDescription>{t('onboarding.newDescription')}</DialogDescription>
        </DialogHeader>

        {created ? (
          <div className="mt-4 space-y-4">
            <CodeReveal code={created.code} />
            <DialogFooter>
              <Button onClick={() => close(false)}>{t('common.close')}</Button>
            </DialogFooter>
          </div>
        ) : templates && templates.length === 0 ? (
          <Alert className="mt-4">{t('onboarding.noTemplates')}</Alert>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-4">
            <Field label={t('onboarding.name')} required>
              <Input value={name} onChange={(event) => setName(event.target.value)} maxLength={120} />
            </Field>
            <Field label={t('onboarding.template')} hint={t('onboarding.templateHint')} required>
              <Select
                value={templateId}
                onChange={(event) => setTemplateId(event.target.value)}
                options={(templates ?? []).map((template) => ({
                  value: template.id,
                  label: template.is_kickoff
                    ? `${template.name} (${t('onboarding.kickoff')})`
                    : template.name,
                }))}
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('onboarding.expiresAt')} required>
                <Input type="date" value={expiresAt} onChange={(event) => setExpiresAt(event.target.value)} />
              </Field>
              <Field label={t('onboarding.maxApplications')} required>
                <Input
                  type="number"
                  min={1}
                  max={ONBOARDING_LIMITS.maxMaxApplications}
                  value={max}
                  onChange={(event) => setMax(event.target.value)}
                />
              </Field>
            </div>
            {library && library.length > 0 && (
              <Field label={t('onboarding.privacyChoice')} hint={t('onboarding.privacyChoiceHint')} required>
                <Select
                  value={noticeChoice}
                  onChange={(event) => setNoticeChoice(event.target.value)}
                  options={[
                    ...library.map((item) => ({
                      value: item.id,
                      label: item.is_default ? `${item.name} (${t('privacyNotices.default')})` : item.name,
                    })),
                    { value: CUSTOM, label: t('onboarding.privacyCustom') },
                  ]}
                />
              </Field>
            )}
            {chosen ? (
              <Field label={t('onboarding.privacyPreview')}>
                <Textarea rows={6} value={chosen.body} readOnly />
              </Field>
            ) : (
              <Field label={t('onboarding.privacyNotice')} hint={t('onboarding.privacyNoticeHint')} required>
                <Textarea
                  rows={5}
                  value={notice}
                  maxLength={ONBOARDING_LIMITS.maxPrivacyNoticeLength}
                  onChange={(event) => setNotice(event.target.value)}
                />
              </Field>
            )}
            {error && <Alert>{error === 'error' ? t('errors.generic') : error}</Alert>}
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => close(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" loading={saving} disabled={!name || !templateId || !expiresAt || (!chosen && !notice)}>
                {t('onboarding.create')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default function OnboardingList() {
  const { t, i18n } = useTranslation()
  const [sessions, setSessions] = useState<OnboardingSession[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { user } = useAuth()
  const isAdmin = isAdminUser(user)
  const [creating, setCreating] = useState(false)
  const [landing, setLanding] = useState(false)

  const load = useCallback(() => {
    return api
      .get<{ data: OnboardingSession[] }>('/api/onboarding/sessions')
      .then((response) => setSessions(response.data))
      .catch((err) => setError(toErrorMessage(err) ?? 'error'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const pendingTotal = sessions.reduce((sum, session) => sum + session.pending_count, 0)

  return (
    <>
      <PageHeader
        title={t('onboarding.title')}
        subtitle={t('onboarding.subtitle')}
        actions={
          isAdmin && (
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => setLanding(true)}>
                <RiPagesLine className="-ml-1 mr-1.5 size-4" aria-hidden="true" />
                {t('onboarding.landingButton')}
              </Button>
              <Button onClick={() => setCreating(true)}>
                <RiAddLine className="-ml-1 mr-1.5 size-4" aria-hidden="true" />
                {t('onboarding.new')}
              </Button>
            </div>
          )
        }
      />

      {loading ? (
        <SectionSpinner />
      ) : error ? (
        <Alert>{error === 'error' ? t('errors.generic') : error}</Alert>
      ) : sessions.length === 0 ? (
        <EmptyState
          icon={<RiUserAddLine className="size-6" aria-hidden="true" />}
          title={t('onboarding.emptyTitle')}
          description={t(isAdmin ? 'onboarding.emptyDescription' : 'onboarding.emptyDescriptionOperator')}
          action={isAdmin ? <Button onClick={() => setCreating(true)}>{t('onboarding.new')}</Button> : undefined}
        />
      ) : (
        <>
          {pendingTotal > 0 && (
            <Alert variant="info" className="mb-5">
              {t('onboarding.waitingBanner', { count: pendingTotal })}
            </Alert>
          )}
          <ul className="grid gap-4 sm:grid-cols-2">
            {sessions.map((session) => (
              <li key={session.id}>
                <Link to={`/app/onboarding/${session.id}`} className="block">
                  <Card className="p-5 transition hover:ring-primary-300 dark:hover:ring-primary-700">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h2 className="truncate text-sm font-semibold text-gray-900 dark:text-gray-50">
                          {session.name}
                        </h2>
                        <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                          {session.survey_title}
                        </p>
                      </div>
                      <SessionStatus session={session} />
                    </div>
                    <dl className="mt-4 grid grid-cols-3 gap-2 text-center">
                      <div>
                        <dd className="text-xl font-semibold text-gray-900 dark:text-gray-50">
                          {session.pending_count}
                        </dd>
                        <dt className="text-xs text-gray-500 dark:text-gray-400">
                          {t('onboarding.pending')}
                        </dt>
                      </div>
                      <div>
                        <dd className="text-xl font-semibold text-gray-900 dark:text-gray-50">
                          {session.accepted_count}
                        </dd>
                        <dt className="text-xs text-gray-500 dark:text-gray-400">
                          {t('onboarding.accepted')}
                        </dt>
                      </div>
                      <div>
                        <dd className="text-xl font-semibold text-gray-900 dark:text-gray-50">
                          {session.rejected_count}
                        </dd>
                        <dt className="text-xs text-gray-500 dark:text-gray-400">
                          {t('onboarding.rejected')}
                        </dt>
                      </div>
                    </dl>
                    <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                      {t('onboarding.until', { date: formatDate(session.expires_at.slice(0, 10), i18n.language) })}
                    </p>
                  </Card>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}

      {isAdmin && <NewSessionDialog open={creating} onOpenChange={setCreating} onCreated={load} />}
      {isAdmin && <LandingSettingsDialog open={landing} onOpenChange={setLanding} />}
    </>
  )
}
